/**
 * 全局错误处理工具
 */

let errorCallback = null

/**
 * 设置全局错误处理
 * @param {Function} callback - 错误回调函数
 */
export function setupGlobalErrorHandler(callback) {
  errorCallback = callback

  // 捕获全局 JavaScript 错误
  window.addEventListener('error', (event) => {
    console.error('Global error:', event.error)
    
    const errorInfo = {
      type: 'error',
      message: event.error?.message || '发生未知错误',
      stack: event.error?.stack,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno
    }

    if (errorCallback) {
      errorCallback(errorInfo)
    }

    // 阻止默认错误处理
    event.preventDefault()
  })

  // 捕获未处理的 Promise 拒绝
  window.addEventListener('unhandledrejection', (event) => {
    console.error('Unhandled promise rejection:', event.reason)
    
    const errorInfo = {
      type: 'promise',
      message: event.reason?.message || '异步操作失败',
      reason: event.reason
    }

    if (errorCallback) {
      errorCallback(errorInfo)
    }

    // 阻止默认错误处理
    event.preventDefault()
  })
}

/**
 * 安全执行计算函数
 * @param {Function} computeFn - 计算函数
 * @param {*} fallbackValue - 失败时的降级值
 * @param {Function} onError - 错误回调
 * @returns {*} 计算结果或降级值
 */
export function safeCompute(computeFn, fallbackValue = null, onError = null) {
  try {
    return computeFn()
  } catch (error) {
    console.error('Computation error:', error)
    
    if (onError) {
      onError(error)
    }

    return fallbackValue
  }
}

/**
 * 安全执行可视化渲染
 * @param {Function} renderFn - 渲染函数
 * @param {Object} options - 选项
 * @returns {boolean} 是否渲染成功
 */
export function safeRender(renderFn, options = {}) {
  const { onError, fallbackUI } = options

  try {
    renderFn()
    return true
  } catch (error) {
    console.error('Rendering error:', error)
    
    if (onError) {
      onError(error)
    }

    if (fallbackUI) {
      fallbackUI()
    }

    return false
  }
}

/**
 * 创建错误边界包装器
 * @param {Function} fn - 要包装的函数
 * @param {Object} options - 选项
 * @returns {Function} 包装后的函数
 */
export function createErrorBoundary(fn, options = {}) {
  const { fallback, onError } = options

  return async (...args) => {
    try {
      return await fn(...args)
    } catch (error) {
      console.error('Error boundary caught:', error)
      
      if (onError) {
        onError(error)
      }

      return fallback !== undefined ? fallback : null
    }
  }
}

/**
 * 显示友好的错误消息
 * @param {Error} error - 错误对象
 * @returns {string} 友好的错误消息
 */
export function getFriendlyErrorMessage(error) {
  if (!error) {
    return '发生未知错误'
  }

  // 网络错误
  if (error.message?.includes('fetch') || error.message?.includes('network')) {
    return '网络连接失败，请检查您的网络设置'
  }

  // 加载错误
  if (error.message?.includes('load') || error.message?.includes('import')) {
    return '资源加载失败，请刷新页面重试'
  }

  // 计算错误
  if (error.message?.includes('NaN') || error.message?.includes('Infinity')) {
    return '计算结果无效，请检查参数设置'
  }

  // 渲染错误
  if (error.message?.includes('canvas') || error.message?.includes('render')) {
    return '可视化渲染失败，请尝试刷新页面'
  }

  // 默认消息
  return error.message || '操作失败，请重试'
}

/**
 * 记录错误日志
 * @param {string} context - 错误上下文
 * @param {Error} error - 错误对象
 * @param {Object} metadata - 额外的元数据
 */
export function logError(context, error, metadata = {}) {
  const errorLog = {
    timestamp: new Date().toISOString(),
    context,
    message: error?.message,
    stack: error?.stack,
    metadata,
    userAgent: navigator.userAgent,
    url: window.location.href
  }

  console.error(`[${context}]`, errorLog)

  // 在生产环境中，可以将错误发送到错误追踪服务（如 Sentry）
  if (import.meta.env.PROD) {
    // sendToErrorTracking(errorLog)
  }
}
